/**
 * Create Transfer Modal Component
 *
 * Modal for creating a new transfer request.
 * @see Sprint 14 - Transferts Inter-Sites
 * @see US-14.5 - UI Liste et Creation
 * @see Sprint 22 - Transferts par Packaging
 */

import { useState, useEffect, useMemo } from 'react';
import { Modal, ModalFooter, Button, Select, Textarea, Input } from '@/components/ui';
import { getQuantityStep } from '@/lib/format';
import { Plus, Trash2 } from 'lucide-react';
import { SearchAutocomplete } from '@/components/ui';
import type { SearchAutocompleteOption } from '@/components/ui';
import { useTransferSites, useCreateTransfer } from '../hooks';
import { useRawProductsSearch } from '@/features/recipes/hooks/useProductsSearch';
import { usePackagingOptions } from '@/hooks';
import type { CreateTransferDto, InventoryPackagingOption } from '@bstock/shared';

interface CreateTransferModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

interface LineState {
  id: string;
  rawProductId: string;
  rawProductName: string;
  quantity: number;
  unit: string;
  // Packaging fields (Sprint 22)
  packagingId: string | null;
  packagingQty: string;
  conversionFactor: number | null;
  packagingOptions: InventoryPackagingOption[];
}

const BASE_UNIT_VALUE = '__base_unit__';

export function CreateTransferModal({
  open,
  onOpenChange,
}: CreateTransferModalProps) {
  const { data: sites = [], isLoading: loadingSites } = useTransferSites();
  const createMutation = useCreateTransfer();

  // Form state
  const [sourceSiteId, setSourceSiteId] = useState('');
  const [notes, setNotes] = useState('');
  const [lines, setLines] = useState<LineState[]>([]);

  // Product search state
  const [productSearch, setProductSearch] = useState('');
  const { data: productOptions = [], isLoading: searchingProducts } = useRawProductsSearch(productSearch);

  // Fetch packaging options for all products in lines
  const rawProductIds = useMemo(
    () => lines.map((l) => l.rawProductId),
    [lines]
  );
  const { data: packagingOptionsMap } = usePackagingOptions(rawProductIds);

  // Update lines with packaging options when data arrives
  useEffect(() => {
    if (!packagingOptionsMap) return;
    setLines((prev) =>
      prev.map((line) => {
        const options = packagingOptionsMap[line.rawProductId] ?? [];
        if (
          options.length !== line.packagingOptions.length ||
          options.some((o, i) => o.packagingId !== line.packagingOptions[i]?.packagingId)
        ) {
          return { ...line, packagingOptions: options };
        }
        return line;
      })
    );
  }, [packagingOptionsMap]);

  // Reset form when modal closes
  useEffect(() => {
    if (!open) {
      setSourceSiteId('');
      setNotes('');
      setLines([]);
      setProductSearch('');
    }
  }, [open]);

  const siteOptions = sites.map((s) => ({
    value: s.id,
    label: `${s.name}${s.city ? ` - ${s.city}` : ''}`,
  }));

  const handleAddLine = (option: SearchAutocompleteOption | null) => {
    if (!option) return;

    // Check if already added
    if (lines.some((l) => l.rawProductId === option.id)) {
      return;
    }

    // Get unit from option metadata if available
    const unit = (option as unknown as { unit?: string }).unit ?? '';

    setLines([
      ...lines,
      {
        id: `temp-${Date.now()}-${Math.random().toString(36).slice(2)}`,
        rawProductId: option.id,
        rawProductName: option.label,
        quantity: 1,
        unit,
        packagingId: null,
        packagingQty: '1',
        conversionFactor: null,
        packagingOptions: packagingOptionsMap?.[option.id] ?? [],
      },
    ]);
    setProductSearch('');
  };

  const handleRemoveLine = (id: string) => {
    setLines(lines.filter((l) => l.id !== id));
  };

  const handleQuantityChange = (id: string, quantity: number) => {
    setLines(lines.map((l) => (l.id === id ? { ...l, quantity } : l)));
  };

  const handlePackagingChange = (lineId: string, value: string) => {
    setLines(lines.map((l) => {
      if (l.id !== lineId) return l;

      if (value === BASE_UNIT_VALUE || value === '') {
        // Switch back to base unit
        return {
          ...l,
          packagingId: null,
          conversionFactor: null,
          packagingQty: String(l.quantity),
        };
      }

      // Find the selected packaging option
      const option = l.packagingOptions.find((o) => o.packagingId === value);
      if (!option) return l;

      return {
        ...l,
        packagingId: value,
        conversionFactor: option.conversionFactor,
        packagingQty: '1',
        quantity: option.conversionFactor, // Auto-update quantity to 1 packaging
      };
    }));
  };

  const handlePackagingQtyChange = (lineId: string, qtyStr: string) => {
    setLines(lines.map((l) => {
      if (l.id !== lineId) return l;
      const qty = parseFloat(qtyStr) || 0;
      return {
        ...l,
        packagingQty: qtyStr,
        quantity: l.conversionFactor ? qty * l.conversionFactor : qty,
      };
    }));
  };

  const handleSubmit = async () => {
    if (!sourceSiteId || lines.length === 0) return;

    const data: CreateTransferDto = {
      sourceSiteId,
      notes: notes || undefined,
      lines: lines.map((l) => {
        if (l.packagingId && l.conversionFactor) {
          const pkgQty = parseFloat(l.packagingQty) || 0;
          return {
            rawProductId: l.rawProductId,
            quantity: pkgQty * l.conversionFactor,
            packagingId: l.packagingId,
            packagingQty: pkgQty,
            conversionFactor: l.conversionFactor,
          };
        }
        return {
          rawProductId: l.rawProductId,
          quantity: l.quantity,
        };
      }),
    };

    await createMutation.mutateAsync(data);
    onOpenChange(false);
  };

  const canSubmit = sourceSiteId && lines.length > 0 && lines.every((l) => l.quantity > 0);

  return (
    <Modal
      isOpen={open}
      onClose={() => onOpenChange(false)}
      title="Nouvelle demande de transfert"
      size="3xl"
    >
      <div className="space-y-6">
        {/* Site + Product search on same row */}
        <div className="flex flex-wrap gap-4">
          <div className="flex-1 min-w-[200px]">
            <label className="block text-sm font-medium text-neutral-700 mb-1">
              Demander a *
            </label>
            <Select
              value={sourceSiteId}
              onChange={setSourceSiteId}
              options={siteOptions}
              placeholder="Selectionner le site fournisseur"
              disabled={loadingSites}
            />
          </div>

          <div className="flex-1 min-w-[200px]">
            <label className="block text-sm font-medium text-neutral-700 mb-1">
              Ajouter un produit *
            </label>
            <SearchAutocomplete
              placeholder="Rechercher un produit..."
              value={null}
              onChange={handleAddLine}
              options={productOptions}
              isLoading={searchingProducts}
              searchValue={productSearch}
              onSearchChange={setProductSearch}
              emptyMessage="Aucun produit trouve"
              minSearchLength={2}
            />
          </div>
        </div>

        {/* Product lines */}
        <div>
          {/* Lines list */}
          {lines.length > 0 ? (
            <div className="space-y-2 max-h-60 overflow-auto">
              {lines.map((line) => {
                const hasPackagingOptions = line.packagingOptions.length > 0;

                return (
                  <div
                    key={line.id}
                    className="flex items-center gap-2 p-2 bg-neutral-50 rounded-lg"
                  >
                    {/* Product name */}
                    <div className="flex-1 min-w-0 font-medium text-sm truncate">
                      {line.rawProductName}
                    </div>
                    {/* Packaging / unit selector */}
                    <div className="w-48 shrink-0">
                      {hasPackagingOptions ? (
                        <Select
                          value={line.packagingId ?? BASE_UNIT_VALUE}
                          onChange={(val) => handlePackagingChange(line.id, val)}
                          options={[
                            { value: BASE_UNIT_VALUE, label: `Unite de base (${line.unit})` },
                            ...line.packagingOptions.map((o) => ({
                              value: o.packagingId,
                              label: o.label,
                            })),
                          ]}
                        />
                      ) : (
                        <div className="h-9 flex items-center text-sm text-neutral-600 px-2">
                          {line.unit}
                        </div>
                      )}
                    </div>
                    {/* Quantity input */}
                    <div className="w-24 shrink-0">
                      {line.packagingId ? (
                        <Input
                          type="number"
                          min="0.01"
                          max={1000}
                          step={getQuantityStep()}
                          value={line.packagingQty}
                          onChange={(e) => {
                            const val = parseFloat(e.target.value);
                            if (!isNaN(val) && val > 1000) return;
                            handlePackagingQtyChange(line.id, e.target.value);
                          }}
                        />
                      ) : (
                        <Input
                          type="number"
                          min="0.01"
                          max={1000}
                          step={getQuantityStep()}
                          value={line.quantity}
                          onChange={(e) => {
                            const val = parseFloat(e.target.value) || 0;
                            handleQuantityChange(line.id, Math.min(val, 1000));
                          }}
                        />
                      )}
                    </div>
                    {/* Delete button */}
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => handleRemoveLine(line.id)}
                      className="shrink-0"
                    >
                      <Trash2 className="w-4 h-4 text-danger-500" />
                    </Button>
                  </div>
                );
              })}
            </div>
          ) : (
            <div className="text-center py-8 text-neutral-400 border-2 border-dashed rounded-lg">
              Aucun produit ajoute
            </div>
          )}
        </div>

        {/* Notes */}
        <div>
          <label className="block text-sm font-medium text-neutral-700 mb-1">
            Notes
          </label>
          <Textarea
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
            placeholder="Notes optionnelles..."
            rows={3}
          />
        </div>
      </div>

      <ModalFooter className='pt-4'>
        <Button variant="secondary" onClick={() => onOpenChange(false)}>
          Annuler
        </Button>
        <Button
          variant="primary"
          onClick={handleSubmit}
          disabled={!canSubmit || createMutation.isPending}
        >
          <Plus className="w-4 h-4 mr-2" />
          Creer la demande
        </Button>
      </ModalFooter>
    </Modal>
  );
}
